import { api } from './api.js'

/**
 * Push notifications on this device (Charles, 2026-09-11: "push where
 * opted in, email as fallback"). Standard Web Push — the phone's own
 * browser feature, no third party — for the end-of-day session-log
 * reminder. The rules a phone imposes, in plain terms:
 *
 *   - iPhone: only the HOME-SCREEN app can receive them (iOS 16.4+), never
 *     Safari itself. In Safari there is no PushManager at all, so that
 *     case reads as 'install' and the button explains Add to Home Screen.
 *   - The permission prompt only appears in response to a tap.
 *   - Once denied, only the phone's Settings can undo it ('denied').
 *
 * The subscription lives in the browser; the server keeps a copy per
 * device so the reminder knows where to send. On every launch the app
 * re-posts it (syncPush) so the copy follows the signed-in account and
 * a subscription iOS quietly renewed still reaches us.
 */

const isIOS = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const isStandalone = () =>
  window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches

const supported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

/**
 * 'on' | 'off' | 'denied' | 'install' | 'unsupported'
 * 'install' = an iPhone in Safari: it can, once added to the Home Screen.
 */
export async function pushState() {
  if (!supported()) return isIOS() && !isStandalone() ? 'install' : 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  const sub = await currentSubscription()
  return sub ? 'on' : 'off'
}

async function registration() {
  // vite-plugin-pwa registers the worker on load; wait for it rather than
  // registering a second copy. Dev (Vite on :5180) has no worker at all.
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 4000))
  return Promise.race([navigator.serviceWorker.ready, timeout])
}

async function currentSubscription() {
  try {
    const reg = await registration()
    return reg ? await reg.pushManager.getSubscription() : null
  } catch {
    return null
  }
}

// The server's public key arrives base64url; the browser wants raw bytes.
function keyBytes(b64url) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4)
  const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

/** Ask, subscribe, tell the server. Returns the new state. Throws with a readable message. */
export async function enablePush() {
  if (!supported()) throw new Error(isIOS() && !isStandalone()
    ? 'Add the app to your Home Screen first, then turn this on from there.'
    : 'This browser can’t receive notifications.')
  // Permission must be requested inside the tap — no awaits before it on
  // iOS, or the prompt never shows. Everything else waits until after.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const reg = await registration()
  if (!reg) throw new Error('Notifications activate on the deployed site (HTTPS).')
  const { key } = await api('/push/key')
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) })
  }
  await api('/push/subscribe', { body: { subscription: sub.toJSON() } })
  return 'on'
}

/** Unsubscribe this device and forget it server-side. */
export async function disablePush() {
  const sub = await currentSubscription()
  if (!sub) return 'off'
  const endpoint = sub.endpoint
  try { await sub.unsubscribe() } catch { /* already gone */ }
  await api('/push/subscribe', { method: 'DELETE', body: { endpoint } }).catch(() => {})
  return 'off'
}

/** Prove delivery without waiting for 6 PM. */
export function sendTestPush() {
  return api('/push/test', { method: 'POST' })
}

// Re-announce this device's subscription once per launch (see above).
let synced = false
export async function syncPush() {
  if (synced || !supported()) return
  synced = true
  try {
    const sub = await currentSubscription()
    if (sub) await api('/push/subscribe', { body: { subscription: sub.toJSON() } })
  } catch {
    /* offline or signed out — next launch */
  }
}
